import { Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';

import { getDesignTokens } from '~c/components/base/core';

export type StatusBadgeVariant = 'risk' | 'alignment';

export type RiskLevel = 'high' | 'medium' | 'low' | 'unavailable';
export type AlignmentLevel =
  | 'aligned'
  | 'not-aligned'
  | 'fully-compliant'
  | 'partially-compliant'
  | 'not-compliant'
  | 'unavailable'
  | 'incomplete';

export type StatusBadgeValue = RiskLevel | AlignmentLevel;

export interface StatusBadgeProps {
  variant: StatusBadgeVariant;
  value: StatusBadgeValue;
  invertLabel?: boolean;
  align?: 'left' | 'right' | 'center' | 'inherit' | 'justify';
  className?: string;
}

interface BadgeStyles {
  backgroundColor: string;
  border?: string;
  textColor: string;
  label: string;
}

function getBadgeStyles(
  variant: StatusBadgeVariant,
  value: StatusBadgeValue,
  invertLabel: boolean,
  t: ReturnType<typeof getDesignTokens>,
  palette: {
    action: { hover: string };
    text: { secondary: string };
  }
): BadgeStyles {
  if (variant === 'alignment') {
    switch (value as AlignmentLevel) {
      case 'fully-compliant':
      case 'aligned':
        return {
          backgroundColor: t.policy.alignedBg,
          border: `1px solid ${t.policy.alignedText}`,
          textColor: t.policy.alignedText,
          label: value === 'aligned' ? 'Aligned' : 'Fully Compliant',
        };
      case 'partially-compliant':
        return {
          backgroundColor: 'rgba(180,83,9,0.12)',
          border: '1px solid #B45309',
          textColor: '#B45309',
          label: 'Partially Compliant',
        };
      case 'unavailable':
        return {
          backgroundColor: palette.action.hover,
          textColor: palette.text.secondary,
          label: 'Unavailable',
        };
      case 'incomplete':
        return {
          backgroundColor: palette.action.hover,
          textColor: palette.text.secondary,
          label: 'Incomplete',
        };
      case 'not-compliant':
      case 'not-aligned':
      default:
        return {
          backgroundColor: t.policy.notAlignedBg,
          border: `1px solid ${t.policy.notAlignedText}`,
          textColor: t.policy.notAlignedText,
          label: value === 'not-aligned' ? 'Not Aligned' : 'Not Compliant',
        };
    }
  }

  switch (value as RiskLevel) {
    case 'high': {
      if (invertLabel) {
        return {
          backgroundColor: t.risk.lowBg,
          border: `1px solid ${t.risk.low}`,
          textColor: t.risk.low,
          label: 'Low',
        };
      }
      return {
        backgroundColor: t.risk.highBg,
        border: `1px solid ${t.risk.high}`,
        textColor: t.risk.high,
        label: 'High',
      };
    }
    case 'medium':
      return {
        backgroundColor: t.risk.mediumBg,
        border: `1px solid ${t.risk.medium}`,
        textColor: t.risk.medium,
        label: 'Med',
      };
    case 'low': {
      if (invertLabel) {
        return {
          backgroundColor: t.risk.highBg,
          border: `1px solid ${t.risk.high}`,
          textColor: t.risk.high,
          label: 'High',
        };
      }
      return {
        backgroundColor: t.risk.lowBg,
        border: `1px solid ${t.risk.low}`,
        textColor: t.risk.low,
        label: 'Low',
      };
    }
    case 'unavailable':
      return {
        backgroundColor: palette.action.hover,
        textColor: palette.text.secondary,
        label: 'Unavailable',
      };
    default:
      return {
        backgroundColor: 'transparent',
        textColor: 'transparent',
        label: '',
      };
  }
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  variant,
  value,
  invertLabel = false,
  align,
  className,
}) => {
  const theme = useTheme();
  const t = getDesignTokens(
    theme.palette.mode === 'dark' ? 'dark' : 'light',
    theme.accentPalette
  );

  const { backgroundColor, border, textColor, label } = getBadgeStyles(
    variant,
    value,
    invertLabel,
    t,
    theme.palette
  );

  if (!label) {
    return null;
  }

  const chipStyles = {
    backgroundColor,
    border: border ?? 'none',
    fontSize: '10px',
    fontStyle: 'normal' as const,
    fontWeight: 600,
    borderRadius: `${t.radiusLarge}px`,
    padding: '4px 12px',
    letterSpacing: '0.02em',
    minWidth:
      variant === 'alignment'
        ? value === 'aligned' || value === 'fully-compliant'
          ? '120px'
          : value === 'partially-compliant'
            ? '152px'
            : '120px'
        : '65px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div className={className} style={{ textAlign: align }}>
      <Chip
        label={
          <span
            style={{
              color: textColor,
              fontSize: chipStyles.fontSize,
              fontStyle: chipStyles.fontStyle,
              fontWeight: chipStyles.fontWeight,
              letterSpacing: chipStyles.letterSpacing,
              textTransform: 'uppercase',
            }}
          >
            {label}
          </span>
        }
        variant="filled"
        sx={chipStyles}
      />
    </div>
  );
};

export default StatusBadge;
